import { Boxes } from 'lucide-react';

import { useElementCameraPlayback } from 'ai-tutor-player';
import type { CameraPose } from 'ai-tutor-player';
import type { ScriptElement, ThreeDContent } from '@/lib/schema.types';

import { ModelViewerCanvas } from './ModelViewerCanvas';

function asThreeD(content: unknown): ThreeDContent {
  if (typeof content === 'string') return { src: content };
  if (content && typeof content === 'object') return content as ThreeDContent;
  return { src: '' };
}

/** The element's authored "home" pose, or null when none was captured. */
function defaultPoseOf(data: ThreeDContent): CameraPose | null {
  if (!data.defaultCameraOrbit) return null;
  return {
    cameraOrbit: data.defaultCameraOrbit,
    cameraTarget: data.defaultCameraTarget,
    fieldOfView: data.fieldOfView,
  };
}

interface Props {
  element: ScriptElement;
  /**
   * When `true`, the viewer fills its parent (used by freeform mode where
   * the wrapper sets explicit width/height). When `false`, it uses the
   * element's `style.width` / `style.height` if set, otherwise falls back to
   * an `aspect-video` box that fills the column width.
   */
  fill?: boolean;
}

export function ThreeDElement({ element, fill }: Props) {
  const data = asThreeD(element.content);
  // When the teach-studio preview is playing, this element may be driven by a
  // camera keyframe timeline; otherwise it's user-controlled (edit mode).
  const playback = useElementCameraPlayback(element.id);

  const w = element.style?.width as number | string | undefined;
  const h = element.style?.height as number | string | undefined;

  const sizingStyle: React.CSSProperties = fill
    ? { width: '100%', height: '100%' }
    : {
        width: w !== undefined ? (typeof w === 'number' ? `${w}px` : w) : '100%',
        height: h !== undefined ? (typeof h === 'number' ? `${h}px` : h) : undefined,
        maxWidth: '100%',
      };
  const sizingClass = fill
    ? 'overflow-hidden rounded-md'
    : h === undefined
      ? 'aspect-video overflow-hidden rounded-md'
      : 'overflow-hidden rounded-md';

  if (!data.src) {
    return (
      <div
        style={sizingStyle}
        className={`${sizingClass} flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-indigo-100 to-purple-100 text-indigo-700`}
      >
        <Boxes size={28} />
        <span className="text-xs font-medium">3D model — paste a .glb / .gltf URL</span>
      </div>
    );
  }

  const inPlayback = playback != null;
  // Playback: camera + labels driven by the timeline, controls off.
  // Edit: user-controlled (or auto-rotate), resting at the default pose if set.
  const pose = inPlayback ? playback.pose : defaultPoseOf(data);
  // model-viewer's interpolation-decay IS the camera damper time constant
  // (default 50; larger = slower). Drive it directly off the chosen move time so
  // the speed slider has an obvious effect (snappy ~120ms → slow glide ~2.6s).
  const interpolationDecay = inPlayback
    ? Math.min(2600, Math.max(60, playback.transitionMs))
    : 100;

  const bgClass = data.transparentBackground
    ? 'bg-transparent'
    : 'bg-gradient-to-br from-slate-100 to-slate-200';
  // Optional vignette: fade the model out toward the edges of its box.
  const fadeMask = data.fadeEdges
    ? 'radial-gradient(ellipse 78% 78% at 50% 50%, #000 58%, transparent 92%)'
    : undefined;

  return (
    <div
      style={{
        position: 'relative',
        ...sizingStyle,
        ...(fadeMask
          ? { maskImage: fadeMask, WebkitMaskImage: fadeMask }
          : {}),
      }}
      className={`${sizingClass} ${bgClass}`}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {data.groundShadow !== false && (
        <div
          // Soft contact-shadow ellipse so the model reads as resting on a
          // surface instead of floating.
          style={{
            position: 'absolute',
            left: '14%',
            right: '14%',
            bottom: '5%',
            height: '14%',
            background:
              'radial-gradient(ellipse at center, rgba(0,0,0,0.32) 0%, rgba(0,0,0,0.18) 38%, rgba(0,0,0,0) 72%)',
            filter: 'blur(3px)',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
      )}
      <ModelViewerCanvas
        src={data.src}
        poster={data.poster}
        alt={data.alt}
        exposure={data.exposure}
        autoRotate={inPlayback ? false : data.autoRotate !== false}
        cameraControls={data.lockCamera ? false : inPlayback ? false : data.cameraControls !== false}
        pose={pose}
        interpolationDecay={interpolationDecay}
        labels={inPlayback ? playback.labels : undefined}
        className="h-full w-full"
        style={{ width: '100%', height: '100%', backgroundColor: 'transparent' }}
      />
    </div>
  );
}
